import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { 
  Microscope, 
  Brain, 
  Apple, 
  Pill, 
  Activity, 
  Share2, 
  Watch,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const pillarDetails = [
  {
    title: 'Lab OS',
    icon: Microscope,
    color: 'blue',
    specs: ['100+ Biomarkers', 'CLIA-Certified', 'Physician Oversight', 'Historical Trend Mapping'],
    tech: 'Integrated API with national lab networks (LabCorp, Quest) for automated result ingest.'
  },
  {
    title: 'AI OS',
    icon: Brain,
    color: 'purple',
    specs: ['Biological Age Scoring', 'Organ Systems Audit', 'Genetic Risk Screening', 'Pharmacogenomics'],
    tech: 'Proprietary ensemble models trained on multi-omic datasets to predict physiological drift.'
  },
  {
    title: 'Food OS',
    icon: Apple,
    color: 'emerald',
    specs: ['Bio-Synced Meal Timers', 'AI Recipe Tuning', 'Physical Fulfillment', 'CGM Real-time Loops'],
    tech: 'Cross-pillar logic connecting Wearable strain data to dynamic carbohydrate/protein ratios.'
  },
  {
    title: 'Supplement OS',
    icon: Pill,
    color: 'amber',
    specs: ['Custom Formulations', 'Dose Verification', 'Interaction Screening', 'Dynamic Subscription'],
    tech: 'Automated marketplace logic mapping deficiency data to precise nutrient protocols.'
  },
  {
    title: 'Movement OS',
    icon: Activity,
    color: 'orange',
    specs: ['Bio-Adaptive Intensity', 'Recovery Periodization', 'Strength/V02 Max Audit', 'Yoga & Longevity'],
    tech: 'Real-time heart rate and HRV feedback loop to adjust exercise intensity automatically.'
  },
  {
    title: 'Social OS',
    icon: Share2,
    color: 'indigo',
    specs: ['Verified Milestones', 'Biological Benchmarking', 'Community Challenges', 'Family Dashboards'],
    tech: 'Secure data-sharing protocol allowing users to compare biological progress in a privacy-first environment.'
  },
  {
    title: 'Wearable OS',
    icon: Watch,
    color: 'cyan',
    specs: ['Unified Data Ingest', 'Performance Stress Audit', 'Sleep Optimization', 'Real-time Alerts'],
    tech: 'Support for Apple Health, Google Fit, Oura, Whoop, and Garmin via a unified GraphQL gateway.'
  }
];

const DetailedPillarsSection: React.FC = () => {
  const getColorClasses = (color: string) => {
    const mappings: Record<string, { bg: string, border: string, text: string, glow: string, glowHover: string }> = {
      blue: { 
        bg: 'bg-blue-600/10', 
        border: 'border-blue-500/30', 
        text: 'text-blue-500', 
        glow: 'bg-blue-500/10', 
        glowHover: 'group-hover:bg-blue-500/20' 
      },
      purple: { 
        bg: 'bg-purple-600/10', 
        border: 'border-purple-500/30', 
        text: 'text-purple-500', 
        glow: 'bg-purple-500/10', 
        glowHover: 'group-hover:bg-purple-500/20' 
      },
      emerald: { 
        bg: 'bg-emerald-600/10', 
        border: 'border-emerald-500/30', 
        text: 'text-emerald-500', 
        glow: 'bg-emerald-500/10', 
        glowHover: 'group-hover:bg-emerald-500/20' 
      },
      amber: { 
        bg: 'bg-amber-600/10', 
        border: 'border-amber-500/30', 
        text: 'text-amber-500', 
        glow: 'bg-amber-500/10', 
        glowHover: 'group-hover:bg-amber-500/20' 
      },
      orange: { 
        bg: 'bg-orange-600/10', 
        border: 'border-orange-500/30', 
        text: 'text-orange-500', 
        glow: 'bg-orange-500/10', 
        glowHover: 'group-hover:bg-orange-500/20' 
      },
      indigo: { 
        bg: 'bg-indigo-600/10', 
        border: 'border-indigo-500/30', 
        text: 'text-indigo-500', 
        glow: 'bg-indigo-500/10', 
        glowHover: 'group-hover:bg-indigo-500/20' 
      },
      cyan: { 
        bg: 'bg-cyan-600/10', 
        border: 'border-cyan-500/30', 
        text: 'text-cyan-500', 
        glow: 'bg-cyan-500/10', 
        glowHover: 'group-hover:bg-cyan-500/20' 
      },
    };
    return mappings[color] || mappings.blue;
  };

  return (
    <section className="py-24 bg-theme-bg">
      <Container>
        <div className="space-y-32">
          {pillarDetails.map((pillar, idx) => {
            const colors = getColorClasses(pillar.color);
            return (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`flex flex-col lg:flex-row gap-16 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Pillar Visual */}
                <Link 
                  to="/ai-lab"
                  className="flex-1 w-full lg:w-1/2 group/card block"
                >
                  <div className={`p-10 rounded-[48px] ${colors.bg} ${colors.border} border-2 relative overflow-hidden transition-all duration-500 group-hover/card:shadow-2xl group-hover/card:shadow-theme-primary/10 group-hover/card:-translate-y-2`}>
                    <div className={`absolute -top-24 -right-24 w-64 h-64 blur-[80px] rounded-full transition-all ${colors.glow} ${colors.glowHover}`} />
                    <pillar.icon className={`w-20 h-20 ${colors.text} mb-8 transform group-hover/card:scale-110 transition-transform duration-500`} />
                    <h3 className="text-4xl font-black text-theme-text-primary mb-6 uppercase tracking-tight">{pillar.title}</h3>
                    <div className="space-y-4">
                      {pillar.specs.map((spec) => (
                        <div key={spec} className="flex items-center space-x-3">
                          <CheckCircle2 className={`w-5 h-5 ${colors.text}`} />
                          <span className="text-xl text-theme-text-secondary font-bold">{spec}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-10 flex items-center gap-2 text-theme-primary font-bold text-sm">
                      Access System Modules <ArrowRight className="w-4 h-4 group-hover/card:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </Link>

                {/* Pillar Technical Narrative */}
                <div className="flex-1">
                  <div className="max-w-xl">
                    <h4 className="text-xs font-black text-theme-text-muted uppercase tracking-[0.3em] mb-6">Technical Specification</h4>
                    <p className="text-2xl text-theme-text-primary font-normal leading-relaxed mb-8">
                      {pillar.tech}
                    </p>
                    <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
                      <p className="text-sm text-theme-text-secondary italic leading-relaxed">
                        "This module integrates horizontal data streams into a vertical clinical output, ensuring the BIOTUNE Health OS remains the source of truth for all biological decisions."
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default DetailedPillarsSection;
